// One ruler for how old a task or workflow is: a short stamp ("40m", "5h",
// "2d", "3w", "4mo", "2y") and a band that warms as the work sits. Mirrored
// by Paperwork::Age in Ruby, so a stamp the server rendered and a stamp this
// code refreshes read the same.
//
// Bands: under a day "fresh" (ink only), 1-3 days "day" (sand), 3-7 days
// "days" (butter), a week or more "stale" (raspberry). Closed work is
// "closed": untinted, and it keeps the age it had when it closed.

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const BANDS = [[DAY, "fresh"], [3 * DAY, "day"], [7 * DAY, "days"]]

export function ageLabel(ms) {
  const age = Math.max(ms, 0)
  if (age < HOUR) return `${Math.max(1, Math.floor(age / MINUTE))}m`
  if (age < DAY) return `${Math.floor(age / HOUR)}h`
  const days = Math.floor(age / DAY)
  if (days < 14) return `${days}d`
  if (days < 56) return `${Math.floor(days / 7)}w`
  if (days < 365) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}

export function ageBand(ms, { closed = false } = {}) {
  if (closed) return "closed"
  return BANDS.find(([limit]) => ms < limit)?.[1] || "stale"
}

const exact = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })

// The hover text: exact times in the viewer's zone, plus how long ago the
// last change was when it differs from creation.
export function ageTitle({ createdAt, updatedAt, closedAt, now = Date.now() }) {
  const lines = [`Created ${exact.format(createdAt)}`]
  if (closedAt) lines.push(`Closed ${exact.format(closedAt)}`)
  else if (updatedAt && updatedAt - createdAt > MINUTE) lines.push(`Updated ${ageLabel(now - updatedAt)} ago (${exact.format(updatedAt)})`)
  return lines.join("\n")
}

const parse = (value) => {
  if (!value) return null
  const ms = typeof value === "number" ? value : Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

// Fills (or creates) the stamp element: text, band and hover title. With
// sinceClosed, closed work reads how long ago it closed (the age_stamp
// helper); without it, the age it had when it closed (a list's Age column).
export function renderAgeStamp(element, { createdAt, updatedAt, closedAt, closed = false, sinceClosed = false, now = Date.now() }) {
  const created = parse(createdAt)
  if (created === null) {
    element.textContent = ""
    return element
  }
  const closedMs = closed ? (parse(closedAt) ?? parse(updatedAt) ?? now) : null
  const age = (closedMs ?? now) - created
  element.textContent = ageLabel(sinceClosed && closedMs !== null ? now - closedMs : age)
  element.dataset.band = ageBand(age, { closed })
  element.title = ageTitle({ createdAt: created, updatedAt: parse(updatedAt), closedAt: closedMs, now })
  return element
}

export function ageStamp(options) {
  const element = document.createElement("span")
  element.className = "age-stamp"
  return renderAgeStamp(element, options)
}
